/**
 * Sync Schedule Helper Functions
 * 
 * Utilities for displaying sync schedule information and converting
 * between frontend SyncSchedule format and API cron expression format.
 */

import { DataSourceConfig, SyncSchedule } from "@/types/dataSource";
import type { UpdateRefreshScheduleRequestApi, ScheduleDetailsResponseApi } from "@/api/models/dataSourceApi";

const DAY_TO_CRON: Record<string, string> = {
  sun: '0',
  mon: '1',
  tue: '2',
  wed: '3',
  thu: '4',
  fri: '5',
  sat: '6',
};

const CRON_TO_DAY: Record<string, string> = {
  '0': 'sun',
  '1': 'mon',
  '2': 'tue',
  '3': 'wed',
  '4': 'thu',
  '5': 'fri',
  '6': 'sat',
};

/**
 * Get a human-readable schedule indicator for a data source
 */
export function getScheduleIndicator(dataSource: DataSourceConfig): string {
  // Use the schedule description from the API if available
  if (dataSource.scheduleDescription) {
    return `Schedule: ${dataSource.scheduleDescription}`;
  }

  const schedule = dataSource.syncSchedule;

  // No schedule configured
  if (!schedule) {
    if (dataSource.type === 'Excel Spreadsheet') {
      return 'Schedule: Manual upload only';
    }
    return 'Schedule: Not configured';
  }

  // Schedule configured
  if (!schedule.enabled || schedule.frequencyMinutes === 0) {
    return 'Schedule: Manual upload only';
  }

  const freq = schedule.frequencyMinutes;
  
  // Convert minutes to readable format
  if (freq === 15) {
    return `Schedule: Every 15 minutes (${schedule.startTime}-${schedule.endTime})`;
  } else if (freq === 30) {
    return `Schedule: Every 30 minutes (${schedule.startTime}-${schedule.endTime})`;
  } else if (freq === 45) {
    return `Schedule: Every 45 minutes (${schedule.startTime}-${schedule.endTime})`;
  } else if (freq === 60) {
    return `Schedule: Every hour (${schedule.startTime}-${schedule.endTime})`;
  } else if (freq === 120) {
    return `Schedule: Every 2 hours (${schedule.startTime}-${schedule.endTime})`;
  } else if (freq === 240) {
    return `Schedule: Every 4 hours (${schedule.startTime}-${schedule.endTime})`;
  } else if (freq === 360) {
    return `Schedule: Every 6 hours (${schedule.startTime}-${schedule.endTime})`;
  } else if (freq === 720) {
    return `Schedule: Every 12 hours (${schedule.startTime}-${schedule.endTime})`;
  } else if (freq === 1440) {
    return `Schedule: Daily at ${schedule.startTime}`;
  } else {
    return `Schedule: Every ${freq} minutes (${schedule.startTime}-${schedule.endTime})`;
  }
}

/**
 * Get default schedule for a data source type
 */
export function getDefaultSchedule(type: string): SyncSchedule {
  switch (type) {
    case 'Access Financials':
      return {
        enabled: true,
        frequencyMinutes: 60, // Hourly
        daysOfWeek: ['mon', 'tue', 'wed', 'thu', 'fri'],
        startTime: '06:00',
        endTime: '18:00',
      };
    case 'Paycircle':
      return {
        enabled: true,
        frequencyMinutes: 1440, // Daily (nightly)
        daysOfWeek: ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'],
        startTime: '22:00',
        endTime: '23:00',
      };
    case 'Plaid':
      return {
        enabled: true,
        frequencyMinutes: 60, // Hourly
        daysOfWeek: ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'],
        startTime: '06:00',
        endTime: '22:00',
      };
    case 'Excel Spreadsheet':
      return {
        enabled: false,
        frequencyMinutes: 0, // Manual only
        daysOfWeek: [],
        startTime: '00:00',
        endTime: '00:00',
      };
    default:
      return {
        enabled: true,
        frequencyMinutes: 60, // Hourly
        daysOfWeek: ['mon', 'tue', 'wed', 'thu', 'fri'],
        startTime: '06:00',
        endTime: '18:00',
      };
  }
}

/**
 * Convert frontend SyncSchedule to API request format.
 * Builds a cron expression from daysOfWeek + frequencyMinutes,
 * and converts startTime/endTime to ISO date strings for the time window.
 */
export function syncScheduleToApiRequest(schedule: SyncSchedule): UpdateRefreshScheduleRequestApi {
  if (!schedule.enabled) {
    return { enabled: false };
  }

  const cronExpression = buildCronExpression(schedule.frequencyMinutes, schedule.daysOfWeek);

  const today = new Date().toISOString().split('T')[0];
  const betweenTimeStart = `${today}T${schedule.startTime}:00`;
  const betweenTimeEnd = `${today}T${schedule.endTime}:00`;

  return {
    enabled: true,
    cronExpression,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    betweenTimeStart,
    betweenTimeEnd,
  };
}

/**
 * Build a standard 5-field cron expression from frequency and days.
 *
 * Examples:
 *   60 min, weekdays  -> "0 * * * 1-5"  (every hour)
 *   15 min, all days  -> "*\/15 * * * *"
 *   120 min, weekdays -> "0 *\/2 * * 1-5"
 *   1440 min, all     -> "0 0 * * *"     (daily at midnight - time window controls actual start)
 */
function buildCronExpression(frequencyMinutes: number, daysOfWeek: string[]): string {
  let minutePart: string;
  let hourPart: string;

  if (frequencyMinutes <= 0) {
    minutePart = '0';
    hourPart = '*';
  } else if (frequencyMinutes < 60) {
    minutePart = `*/${frequencyMinutes}`;
    hourPart = '*';
  } else if (frequencyMinutes === 60) {
    minutePart = '0';
    hourPart = '*';
  } else if (frequencyMinutes < 1440) {
    const hours = Math.floor(frequencyMinutes / 60);
    minutePart = '0';
    hourPart = `*/${hours}`;
  } else {
    minutePart = '0';
    hourPart = '0';
  }

  const dayPart = buildCronDayPart(daysOfWeek);

  return `${minutePart} ${hourPart} * * ${dayPart}`;
}

function buildCronDayPart(daysOfWeek: string[]): string {
  if (daysOfWeek.length === 0 || daysOfWeek.length === 7) {
    return '*';
  }

  const cronDays = daysOfWeek
    .map((d) => DAY_TO_CRON[d])
    .filter((d): d is string => d !== undefined)
    .map(Number)
    .sort((a, b) => a - b);

  if (cronDays.length === 0) {
    return '*';
  }

  const isConsecutive = cronDays.every((val, i) => i === 0 || val === cronDays[i - 1] + 1);
  if (isConsecutive && cronDays.length > 1) {
    return `${cronDays[0]}-${cronDays[cronDays.length - 1]}`;
  }

  return cronDays.join(',');
}

/**
 * Parse an API schedule response back into the frontend SyncSchedule format.
 */
export function apiScheduleToSyncSchedule(schedule: ScheduleDetailsResponseApi): SyncSchedule {
  if (!schedule.enabled || !schedule.cronExpression) {
    return {
      enabled: false,
      daysOfWeek: [],
      startTime: '00:00',
      endTime: '00:00',
      frequencyMinutes: 0,
    };
  }

  const frequencyMinutes = parseFrequencyFromCron(schedule.cronExpression);
  const daysOfWeek = parseDaysFromCron(schedule.cronExpression);

  let startTime = '06:00';
  let endTime = '18:00';

  if (schedule.betweenTimeStart) {
    startTime = extractTimeFromIso(schedule.betweenTimeStart);
  }
  if (schedule.betweenTimeEnd) {
    endTime = extractTimeFromIso(schedule.betweenTimeEnd);
  }

  return {
    enabled: true,
    frequencyMinutes,
    daysOfWeek,
    startTime,
    endTime,
  };
}

function extractTimeFromIso(isoString: string): string {
  const dateObj = new Date(isoString);
  if (isNaN(dateObj.getTime())) {
    const timePart = isoString.split('T')[1];
    if (timePart) {
      return timePart.substring(0, 5);
    }
    return '00:00';
  }
  const hours = dateObj.getHours().toString().padStart(2, '0');
  const minutes = dateObj.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
}

function parseFrequencyFromCron(cronExpression: string): number {
  const parts = cronExpression.split(' ');
  if (parts.length < 5) return 60;

  const minutePart = parts[0];
  const hourPart = parts[1];

  if (minutePart.startsWith('*/')) {
    return parseInt(minutePart.substring(2), 10) || 60;
  }

  if (hourPart.startsWith('*/')) {
    const hours = parseInt(hourPart.substring(2), 10) || 1;
    return hours * 60;
  }

  if (hourPart === '0' && minutePart === '0') {
    return 1440;
  }

  if (hourPart === '*' && minutePart === '0') {
    return 60;
  }

  return 60;
}

function parseDaysFromCron(cronExpression: string): string[] {
  const parts = cronExpression.split(' ');
  if (parts.length < 5) return ['mon', 'tue', 'wed', 'thu', 'fri'];

  const dayPart = parts[4];

  if (dayPart === '*') {
    return ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
  }

  const dayNumbers: number[] = [];

  const segments = dayPart.split(',');
  for (const segment of segments) {
    if (segment.includes('-')) {
      const [start, end] = segment.split('-').map(Number);
      for (let i = start; i <= end; i++) {
        dayNumbers.push(i);
      }
    } else {
      const num = parseInt(segment, 10);
      if (!isNaN(num)) {
        dayNumbers.push(num);
      }
    }
  }

  return dayNumbers
    .sort((a, b) => a - b)
    .map((n) => CRON_TO_DAY[n.toString()])
    .filter((d): d is string => d !== undefined);
}
